'use client';

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { ArticleThemesEnum, ArticleTopicsEnum } from '@sola/contentful-model/lib/taxonomy-values.ts';
import type { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.ts';
import { useListArticlesQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import type { ArticleSearchInput, SearchResultProps } from '@sola/skippy-search-sdk/lib/schema.ts';
import { ArticleSortByEnum, useArticleQueryParams, usePaginationQueryParams } from '../../utils/query-params.tsx';
export type UseArticleListOptions = {
  articleType?: string;
  country?: string;
  theme?: string;
  topic?: string;
  limit?: number;
  variant?: 'default' | 'related';
  excludeContentfulEntryID?: string;
  hidePagination?: boolean;
  /** When true, filters are read from URL params (dynamic). When false, uses static CMS props. */
  isDynamic?: boolean;
  onArticleLoad?: (info: {
    totalCount: number;
  }) => void;
};
const sortByValues = {
  featured: ArticleSortByEnum.enum.promotedContent,
  ascending: ArticleSortByEnum.enum.asc,
  descending: ArticleSortByEnum.enum.desc
} as const;

/** Derive column counts from limit and variant. */
export const buildColumnLayout = (limit: number, variant: 'default' | 'related') => ({
  columns: 1,
  columnsMd: Math.min(limit, 3),
  columnsLg: variant === 'related' ? Math.min(limit, 3) : Math.min(limit, 4)
});

/** Build the search query config from resolved filter values. */
export const buildArticleQueryConfig = ({
  articleType,
  topic,
  theme,
  country,
  excludeContentfulEntryID,
  sortBy,
  variant
}: {
  articleType?: string;
  topic?: string | null;
  theme?: string | null;
  country?: string | null;
  excludeContentfulEntryID?: string;
  sortBy: string;
  variant: 'default' | 'related';
}): ArticleSearchInput => {
  const isFeaturedSort = sortBy === sortByValues.featured;
  const sortDir: 'asc' | 'desc' = sortBy === sortByValues.ascending ? 'asc' : 'desc';
  const isRelated = variant === 'related';
  return {
    articleType: articleType || undefined,
    topics: !isRelated && topic ? [topic] : undefined,
    themes: !isRelated && theme ? [theme] : undefined,
    country: isRelated ? undefined : country || undefined,
    // For related articles, taxonomy fields boost relevance rather than filter.
    boostTopics: isRelated && topic ? [topic] : undefined,
    boostThemes: isRelated && theme ? [theme] : undefined,
    boostCountry: isRelated ? country || undefined : undefined,
    excludeEntryId: excludeContentfulEntryID || undefined,
    promoted: isFeaturedSort || isRelated ? true : undefined,
    sort: 'datePublished',
    sortDir: isFeaturedSort ? 'desc' : sortDir
  };
};
export const useArticleList = t0 => {
  const $ = _c(35);
  const {
    articleType,
    country,
    theme,
    topic,
    limit,
    variant: t1,
    excludeContentfulEntryID,
    hidePagination: t2,
    isDynamic: t3,
    onArticleLoad
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const hidePagination = t2 === undefined ? false : t2;
  const isDynamic = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== limit) {
    t4 = {
      perPage: limit
    };
    $[0] = limit;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    articleFilters
  } = useArticleQueryParams(t4);
  const {
    currentPage
  } = usePaginationQueryParams();
  const sortBy = isDynamic ? articleFilters.sortby || "desc" : "desc";
  const resolvedLimit = isDynamic ? !hidePagination && articleFilters.limit || limit || 12 : limit || 12;
  const resolvedTopic = topic || (isDynamic ? articleFilters.topic : null);
  const resolvedTheme = theme || (isDynamic ? articleFilters.theme : null);
  const resolvedCountry = country || (isDynamic ? articleFilters.country : null);
  let t5;
  if ($[2] !== resolvedLimit || $[3] !== variant) {
    t5 = buildColumnLayout(resolvedLimit, variant);
    $[2] = resolvedLimit;
    $[3] = variant;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const columnLayout = t5;
  let t6;
  if ($[5] !== articleType || $[6] !== excludeContentfulEntryID || $[7] !== resolvedCountry || $[8] !== resolvedTheme || $[9] !== resolvedTopic || $[10] !== sortBy || $[11] !== variant) {
    t6 = buildArticleQueryConfig({
      articleType,
      topic: resolvedTopic,
      theme: resolvedTheme,
      country: resolvedCountry,
      excludeContentfulEntryID,
      sortBy,
      variant
    });
    $[5] = articleType;
    $[6] = excludeContentfulEntryID;
    $[7] = resolvedCountry;
    $[8] = resolvedTheme;
    $[9] = resolvedTopic;
    $[10] = sortBy;
    $[11] = variant;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const queryConfig = t6;
  const t7 = isDynamic ? currentPage * resolvedLimit : 0;
  let t8;
  if ($[13] !== queryConfig || $[14] !== resolvedLimit || $[15] !== t7) {
    t8 = {
      ...queryConfig,
      perPage: resolvedLimit,
      offset: t7
    };
    $[13] = queryConfig;
    $[14] = resolvedLimit;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      placeholderData: keepPreviousData
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const articleQuery = useListArticlesQuery(t8, t9);
  const {
    data: articleData,
    isLoading,
    isError
  } = articleQuery;
  const articles = articleData?.items;
  const totalCount = articleData?.totalCount || 0;
  const showLoading = isLoading && !articleData;
  const shouldShowPagination = isDynamic && variant === "default" && !hidePagination;
  let t10;
  let t11;
  if ($[18] !== isLoading || $[19] !== onArticleLoad || $[20] !== totalCount) {
    t10 = () => {
      if (onArticleLoad && !isLoading) {
        onArticleLoad({
          totalCount
        });
      }
    };
    t11 = [totalCount, isLoading, onArticleLoad];
    $[18] = isLoading;
    $[19] = onArticleLoad;
    $[20] = totalCount;
    $[21] = t10;
    $[22] = t11;
  } else {
    t10 = $[21];
    t11 = $[22];
  }
  useEffect(t10, t11);
  let t12;
  if ($[23] !== articles) {
    t12 = articles?.filter(_temp);
    $[23] = articles;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const uniqueArticles = t12;
  let t13;
  if ($[25] !== articleData || $[26] !== articleType || $[27] !== columnLayout || $[28] !== isError || $[29] !== resolvedLimit || $[30] !== shouldShowPagination || $[31] !== showLoading || $[32] !== totalCount || $[33] !== uniqueArticles) {
    t13 = {
      articles: uniqueArticles,
      articleData,
      isLoading: showLoading,
      isError,
      totalCount,
      resolvedLimit,
      columnLayout,
      shouldShowPagination,
      articleType
    };
    $[25] = articleData;
    $[26] = articleType;
    $[27] = columnLayout;
    $[28] = isError;
    $[29] = resolvedLimit;
    $[30] = shouldShowPagination;
    $[31] = showLoading;
    $[32] = totalCount;
    $[33] = uniqueArticles;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
};
export const getEyebrow = (result: SearchResultProps, articleType: string | undefined, t: (key: string) => string): string | undefined => {
  if (!articleType && result.taxonomyArticleType) {
    return t(`articles:taxonomy.articleTypes.${result.taxonomyArticleType as ArticleTypesEnum}.singularName`);
  }
  if (result.taxonomyTopic) {
    return t(`articles:taxonomy.articleTopics.${result.taxonomyTopic[0] as ArticleTopicsEnum}.name`);
  }
  if (result.taxonomyArticleTheme) {
    return t(`articles:taxonomy.articleThemes.${result.taxonomyArticleTheme[0] as ArticleThemesEnum}.name`);
  }
  if (result.taxonomyArticleType) {
    return t(`articles:taxonomy.articleTypes.${result.taxonomyArticleType as ArticleTypesEnum}.singularName`);
  }
  return undefined;
};
function _temp(a, i, arr) {
  return arr.findIndex(b => b.contentfulEntryID === a.contentfulEntryID) === i;
}